// Splits scene assistant output into multi-shot cards. Assistants write a
// shot setup followed by timed ranges ("00:00 to 00:04 - ..."); the setup is
// folded into the first card and each range becomes one card.
import {
  buildNodePromptSectionTimeline,
  createEmptyNodePromptSection,
  formatNodePromptTimestamp,
  normalizePromptSectionHeadline,
} from '@/etc/nodePromptDraft'

// Line-leading range: "00:00 to 00:04", "0:00-0:04", "00:00 – 00:04",
// optionally bulleted or bolded, followed by a dash/colon separator.
const RANGE_LINE_RE =
  /^[\s>*_•-]*(\d{1,2}):(\d{2})\s*(?:to|–|—|-|~)\s*(\d{1,2}):(\d{2})\s*\**\s*[—–:-]?\s*/i

// Card headline right after the range: "[Rooftop Leap]", optionally bolded,
// optionally followed by its own separator.
const HEADLINE_RE = /^\**\s*\[([^\]\n]+)\]\s*\**\s*[—–:-]?\s*/

const toSeconds = (min, sec) => Number(min) * 60 + Number(sec)

const stripMarkdown = (text) =>
  `${text || ''}`.replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1').trim()

export function parseTimedShots(text) {
  const lines = `${text || ''}`.split(/\r?\n/)
  let setup = []
  const shots = []
  lines.forEach((line) => {
    const match = line.match(RANGE_LINE_RE)
    if (match) {
      const rest = line.slice(match[0].length)
      const headline = rest.match(HEADLINE_RE)
      shots.push({
        start: toSeconds(match[1], match[2]),
        end: toSeconds(match[3], match[4]),
        headline: headline ? normalizePromptSectionHeadline(headline[1]) : '',
        lines: [headline ? rest.slice(headline[0].length) : rest],
      })
      return
    }
    if (shots.length) shots[shots.length - 1].lines.push(line)
    else setup.push(line)
  })
  setup = stripMarkdown(setup.join('\n'))
  return {
    setup,
    shots: shots.map((shot) => ({
      start: shot.start,
      end: shot.end,
      headline: shot.headline,
      text: stripMarkdown(shot.lines.join('\n')),
    })),
  }
}

// Returns prompt-draft sections, or null when the text has no timed ranges
// (callers then keep it as a single prompt). A card lasts until the next
// range starts — assistants often overlap ranges ("00:03 to 00:07") and the
// cards play back to back. Ranges beyond `maxSections` merge into the last.
export function sectionsFromAssistantText(text, { maxSections = 10 } = {}) {
  const { setup, shots } = parseTimedShots(text)
  if (!shots.length) return null

  const max = Math.max(1, Math.floor(Number(maxSections) || 10))
  let cards = shots
  if (shots.length > max) {
    const rest = shots.slice(max - 1)
    cards = [
      ...shots.slice(0, max - 1),
      {
        start: rest[0].start,
        end: rest[rest.length - 1].end,
        headline: rest[0].headline,
        text: rest.map((shot) => shot.text).filter(Boolean).join('\n\n'),
      },
    ]
  }

  return cards.map((shot, index) => {
    const next = cards[index + 1]
    let duration = next ? next.start - shot.start : shot.end - shot.start
    if (!(duration > 0)) duration = shot.end - shot.start
    const body = index === 0 ? [setup, shot.text] : [shot.text]
    return {
      ...createEmptyNodePromptSection(index + 1),
      headline: shot.headline,
      text: body.filter(Boolean).join('\n\n'),
      duration: duration > 0 ? duration : null,
    }
  })
}

// Inverse for follow-ups: renders the node's cards back in the assistant's
// own "00:00 to 00:04 - [Headline] ..." format so revisions build on the
// current cards.
export function assistantTextFromSections(sections, composeText = (t) => t) {
  return buildNodePromptSectionTimeline(sections)
    .map(({ section, start, end, duration }) => {
      const text = `${composeText(section.text) || ''}`.trim()
      if (!text) return ''
      if (!(duration > 0)) return text
      const headline = section.headline ? `[${section.headline}] ` : ''
      return `${formatNodePromptTimestamp(start)} to ${formatNodePromptTimestamp(end)} - ${headline}${text}`
    })
    .filter(Boolean)
    .join('\n\n')
}
